import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from 'antd';
import {
  formatUnits,
  parseUnits,
} from 'ethers';
import { useTranslation } from 'react-i18next';

import { tokenArr } from '@/collocate';
import Icon from '@/components/Icon';
import InputBoxFrom from '@/components/InputBoxFrom';
import InputBoxTo from '@/components/InputBoxTo';
import ScrollView from '@/components/ScrollView';
import Setting from '@/components/Setting';
import useBestTradeExactIn from '@/hooks/useBestTradeExactIn';
import useErc20Contract from '@/hooks/useErc20Contract';
import useGetAllowanceToken from '@/hooks/useGetAllowanceToken';
import useGetTokenBalance from '@/hooks/useGetTokenBalance';
import useControllablePolling from '@/hooks/usePollingEffect';
import useSwap from '@/hooks/useSwap';
import sortArrow from '@/image/sortArrow.svg';
import useTransactionSetting from '@/store/transactionSetting';
import useConnectWallet from '@/store/wallet/useConnectWallet';
import useWalletPop from '@/store/walletPop';
import {
  divideDecimal,
  formatTo6Decimals,
  multiplyDecimal,
  subtractDecimal,
} from '@/utils';

import Cost from './component/Cost';

export default function Home() {
  const { t } = useTranslation()
  // 从全局交易设置中获取滑点和交易时限
  const {
    transactionSetting: { swapSlippage, swapTradingHour },
  } = useTransactionSetting();
  // 钱包弹窗控制函数
  const { setOpenWallet } = useWalletPop();

  // 钱包地址
  const { address } = useConnectWallet();

  // 用户输入的兑换数量（from和to）
  const [fromAmount, setFromAmount] = useState<string>('');
  const [toAmount, setToAmount] = useState<string>('');

  // 用户选择的代币索引，初始时 fromTokenIndex 默认选第一个，toTokenIndex 未选（-1）
  const [fromTokenIndex, setFromTokenIndex] = useState<number>(0);
  const [toTokenIndex, setToTokenIndex] = useState<number>(-1);
  // from address
  const fromTokenAddress = useMemo(() => {
    return fromTokenIndex < 0 ? '' : tokenArr[fromTokenIndex].address
  }, [fromTokenIndex])
  // to address
  const toTokenAddress = useMemo(() => {
    return toTokenIndex < 0 ? '' : tokenArr[toTokenIndex].address
  }, [toTokenIndex])
  const fromTokenSymbol = useMemo(() => {
    return fromTokenIndex < 0 ? '' : tokenArr[fromTokenIndex].symbol
  }, [fromTokenIndex])
  // to symbol
  const toTokenSymbol = useMemo(() => {
    return toTokenIndex < 0 ? '' : tokenArr[toTokenIndex].symbol
  }, [toTokenIndex])
  // from 输入框的 ref
  const inputBoxRefFrom = useRef<any>(null);
  // to 输入框的 ref
  const inputBoxRefTo = useRef<any>(null);
  // 获取兑换输出数量的接口
  // 判断from地址和to地址是否在useQuote反转 false 表示从from兑换到to，true表示从to兑换到from
  const [isAmountsOutReverse, setIsAmountsOutReverse] = useState(false)
  const { bestTrade, bestTradeExactInLod, getBestTradePath } = useBestTradeExactIn(
    !isAmountsOutReverse ? fromTokenAddress : toTokenAddress,
    isAmountsOutReverse ? fromTokenAddress : toTokenAddress,
    !isAmountsOutReverse ? (fromAmount ? parseUnits(fromAmount) : BigInt(0)) :
      (toAmount ? parseUnits(toAmount) : BigInt(0))
  )
  // ERC20 合约实例，用于查询授权额度和发起授权
  const contractToken = useErc20Contract(fromTokenAddress);

  // 获取授权额度，发起授权，及相关loading状态
  const { allowance, allowanceLod, approve, allowanceLodFun, getAllowance } = useGetAllowanceToken(contractToken);
  // 获取当前代币的余额
  const {
    tokenBalance,
    tokenBalanceLod,
    getTokenBalance
  } = useGetTokenBalance(contractToken);

  // 兑换功能相关hook
  const { swap, swapLod } = useSwap(() => {
    setFromAmount('');
    setToAmount('');
    getTokenBalance();
    inputBoxRefFrom.current.getBestTradePathCall();
    inputBoxRefTo.current.getAmountsOutCall();
    getAllowance();
  });


  /**
   * 输入数量变化时调用
   * @param val 用户输入的数量字符串
   * @param index 0为from输入框，1为to输入框
   */
  const handleAmountChange = async (val: string, index: 0 | 1) => {
    const setFun1 = [setFromAmount, setToAmount];
    // 如果代币未选择，则仅更新对应输入框的数量，不触发兑换量计算
    const is = fromTokenAddress && toTokenAddress
    setFun1[index](val);
    if (!is) return;
    // 标记当前是哪个输入框触发变化
    setIsAmountsOutReverse(!!index);
  };

  /**
   * 处理代币选择变化
   * @param val 选中的代币索引
   * @param index 0 表示 from 代币，1 表示 to 代币
   */
  const handleTokenChange = async (val: number, index: 0 | 1) => {
    const setFunTokenVal = [setFromTokenIndex, setToTokenIndex];
    const setFunToken = [
      () => setFromTokenIndex(toTokenIndex),
      () => setToTokenIndex(fromTokenIndex),
    ];
    const setFunAmount = [
      () => setFromAmount(toAmount),
      () => setToAmount(fromAmount),
    ];

    // 判断是否选择了相同的代币
    const isSameTokenSelected = val === fromTokenIndex || val === toTokenIndex;
    // 判断是否处于初始状态
    const isInitialState = (!fromAmount && !toAmount) || !fromTokenAddress || !toTokenAddress;
    if (isSameTokenSelected) {
      setIsAmountsOutReverse(false)
      if (isInitialState) {
        // 初始状态下切换代币，互换索引和数量
        console.log(1);
        setFunToken[0]();
        setFunToken[1]();
        setFunAmount[0]();
        setFunAmount[1]();
      } else {
        // 已有输入时切换代币，互换索引并重新获取兑换数量
        console.log(2);
        setFunToken[0]();
        setFunToken[1]();
        isAmountsOutReverse ? setFunAmount[1]() : setFunAmount[0]();
      }
      return;
    }
    // 正常选择代币流程
    setFunTokenVal[index](val);
  };

  /**
   * 计算 swap 需要的参数，供调用 swap 接口时使用
   */
  const swapData = useMemo(() => {
    // 如果任何必要条件不满足，返回空数据
    if (!fromAmount || !toAmount || !swapSlippage || !fromTokenAddress || !toTokenAddress) {
      return {
        amountIn: BigInt(0),
        amountOutMin: BigInt(0),
        fromAddress: '',
        toAddress: '',
        deadline: BigInt(0),
        path: []
      };
    }

    // 输入数量转换为合约中使用的单位（BigInt）
    const amountIn = parseUnits(fromAmount);

    // 计算滑点比例（小数）
    const slippage = divideDecimal(swapSlippage, 100);
    // 最小接受数量 = toAmount * (1 - 滑点)，保留6位小数并转换单位
    const amountOutMin = parseUnits(formatTo6Decimals(multiplyDecimal(toAmount, subtractDecimal(1, slippage)), 18));
    // 获取代币合约地址
    const fromAddress = fromTokenAddress;
    const toAddress = toTokenAddress;

    // 计算交易截止时间，单位为分钟，转换为合约中用的单位
    const deadline = parseUnits(multiplyDecimal(swapTradingHour, 60));
    // 交易路径
    const path = isAmountsOutReverse ? bestTrade.path.reverse() : bestTrade.path
    return { amountIn, amountOutMin, fromAddress, toAddress, deadline, path };
  }, [fromAmount, toAmount, swapSlippage, fromTokenAddress, toTokenAddress, swapTradingHour, isAmountsOutReverse, bestTrade.path]);

  /**
   * 兑换按钮点击事件
   * @param type 1-连接钱包，2-授权，3-兑换
   */
  const handleBtnClick = (type: number) => {
    const handlers = [
      setOpenWallet, // 打开钱包连接弹窗
      () => approve({ success: t('home.approvalSuccess'), error: t('home.approvalFailed') }), // 授权代币
      () => {
        // 执行兑换操作
        const { amountIn, amountOutMin, path, deadline } = swapData;
        swap(amountIn, amountOutMin, path, deadline, {
          success: t('home.swapSuccess'),
          error: t('home.swapFailed'),
        });
      },
    ];
    handlers[type - 1]?.();
  };
  /**
   * 根据当前状态决定按钮显示文本、禁用状态和触发动作类型
   */
  const btnLodtext = useMemo(() => {
    if (!address) return { loading: false, text: t('home.connectWallet'), disabled: false, type: 1 };
    if (!fromTokenAddress || !toTokenAddress) return { loading: false, text: t('home.pleaseSelectToken'), disabled: true, type: 0 };
    if (bestTradeExactInLod) return { loading: true, text: t('home.fetchingLiquidityAndPrice'), disabled: true, type: 0 };
    if (!bestTrade.path.length) return { loading: false, text: t('home.insufficientLiquidity'), disabled: true, type: 0 };
    if (!fromAmount) return { loading: false, text: t('home.pleaseEnterSwapAmount'), disabled: true, type: 0 };
    if (tokenBalanceLod) return { loading: true, text: t('home.fetchingBalance'), disabled: true, type: 0 };
    if (parseUnits(fromAmount) > tokenBalance) return { loading: false, text: t('home.insufficientBalance'), disabled: true, type: 0 };
    if (allowanceLod) return { loading: allowanceLod, text: t('home.updatingAllowance'), disabled: true, type: 0 };
    if (allowanceLodFun) return { loading: true, text: t('home.approving'), disabled: true, type: 2 };
    if (allowance < parseUnits(fromAmount)) return { loading: false, text: t('home.approve'), disabled: false, type: 2 };
    if (swapLod) return { loading: true, text: t('home.swapping'), disabled: true, type: 0 };
    return { loading: false, text: t('home.swap'), disabled: false, type: 3 };
  }, [address, t, fromTokenAddress, toTokenAddress, bestTradeExactInLod, bestTrade.path.length, fromAmount, tokenBalanceLod, tokenBalance, allowanceLod, allowanceLodFun, allowance, swapLod]);
  // 交换两个代币
  const transition = () => {
    // debugger
    handleTokenChange(fromTokenIndex, 1)
  }
  useEffect(() => {
    const amount = bestTrade?.amountOut ? formatUnits(bestTrade.amountOut) : '';
    isAmountsOutReverse ? setFromAmount(amount) : setToAmount(amount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestTrade.amountOut])
  useControllablePolling(() => {
    getBestTradePath()
  }, 10000, (!bestTradeExactInLod && bestTrade.path && !bestTradeExactInLod && !swapLod), false)
  return (
    <ScrollView px="168px" className="px-4.25 mt-8  md:max-w-xl md:mx-auto md:h-auto  md:mt-16">
      {/* 设置面板 */}

      <div className="mb-2.5">
        <Setting type={0} />
      </div>

      <div className="relative">
        {/* 输入框 - from */}
        <div className="mb-5 md:mb-10">
          <InputBoxFrom
            onFocusF={() => setIsAmountsOutReverse(false)}
            type={0}
            inputLod={isAmountsOutReverse ? bestTradeExactInLod : false}
            tokenBalance={tokenBalance}
            tokenBalanceLod={tokenBalanceLod}
            ref={inputBoxRefFrom}
            tokenAddressIndex={fromTokenIndex}
            getSelectedTokenIndex={(index) => handleTokenChange(index, 0)}
            amountFrom={fromAmount}
            setAmountFrom={(val) => handleAmountChange(val, 0)}
          />
        </div>

        {/* 代币交换箭头 */}
        <div onClick={transition} className="w-7.5 h-7.5 bg-f7 rounded-full flex items-center justify-center absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <Icon className="w-4 cursor-pointer" src={sortArrow} />
        </div>

        {/* 输入框 - to */}
        <InputBoxTo
          onFocusF={() => setIsAmountsOutReverse(true)}
          type={0}
          ref={inputBoxRefTo}
          inputLod={isAmountsOutReverse ? false : bestTradeExactInLod}
          tokenAddressIndex={toTokenIndex}
          getSelectedTokenIndex={(index) => handleTokenChange(index, 1)}
          amountTo={toAmount}
          setAmountFrom={(val) => handleAmountChange(val, 1)}
        />
      </div>

      {/* 操作按钮 */}
      <Button
        className="mt-4 h-12.5 rounded-10 mb-5 md:shadow-md md:mt-10"
        disabled={btnLodtext.disabled}
        loading={btnLodtext.loading}
        block
        onClick={() => handleBtnClick(btnLodtext.type)}
      >
        {btnLodtext.text}
      </Button>
      <Cost amountIn={swapData.amountIn} amountOutMin={swapData.amountOutMin} fromAddress={swapData.fromAddress} toAddress={swapData.toAddress} fromSymbol={fromTokenSymbol} toSymbol={toTokenSymbol} slippage={swapSlippage} path={{ ...bestTrade }.path} />
    </ScrollView>
  );
}